import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/form';
import { useCheckPermissions } from '../../../hooks/useCheckPermissions';
import { useToast } from '../../../components/ui/use-toast';
import { campaignService } from '../../../services/crowdfunding';
import { supabase } from '../../../lib/supabase';
import { ArrowLeft, Save } from 'lucide-react';

// Schéma de validation pour le formulaire
const campaignSchema = z.object({
  title: z.string().min(5, 'Le titre doit contenir au moins 5 caractères').max(100, 'Le titre ne peut pas dépasser 100 caractères'),
  description: z.string().min(20, 'La description doit contenir au moins 20 caractères'),
  contestant_id: z.string().min(1, 'Veuillez sélectionner un candidat'),
  goal_amount: z.coerce.number().min(1000, 'L\'objectif doit être d\'au moins 1000 FCFA'),
  start_date: z.string().min(1, 'Veuillez sélectionner une date de début'),
  end_date: z.string().min(1, 'Veuillez sélectionner une date de fin'),
  status: z.enum(['draft', 'active', 'completed', 'cancelled']).default('draft'),
  tenant_id: z.string().min(1, 'ID du tenant requis')
});

type CampaignFormValues = z.infer<typeof campaignSchema>;

export const CampaignForm: React.FC = () => {
  useCheckPermissions(['admin']);
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contestants, setContestants] = useState<any[]>([]);
  const [isLoadingContestants, setIsLoadingContestants] = useState(true);
  
  // Récupérer les détails de la campagne si on est en mode édition
  const { data: campaign, isLoading: isLoadingCampaign } = useQuery({
    queryKey: ['campaign-edit', slug],
    queryFn: async () => {
      if (!slug) return null;
      return await campaignService.getCampaignBySlug(slug);
    },
    enabled: !!slug
  });
  
  // Initialiser le formulaire
  const form = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignSchema),
    defaultValues: {
      title: '',
      description: '',
      contestant_id: '',
      goal_amount: 1000,
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: 'draft',
      tenant_id: import.meta.env.VITE_TENANT_ID || '2'
    }
  });
  
  // Charger les candidats
  useEffect(() => {
    const fetchContestants = async () => {
      setIsLoadingContestants(true);
      try {
        const { data, error } = await supabase
          .from('contestants')
          .select(`
            id,
            name,
            unique_code,
            contest:contests (
              name,
              status
            )
          `)
          .eq('contests.status', 'active');
        
        if (error) throw error;
        
        setContestants(data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des candidats:', error);
        toast({
          title: "Erreur",
          description: "Impossible de charger la liste des candidats.",
          variant: "destructive"
        });
      } finally {
        setIsLoadingContestants(false);
      }
    };
    
    fetchContestants();
  }, [toast]);
  
  // Remplir le formulaire avec les données de la campagne en mode édition
  useEffect(() => {
    if (campaign) {
      form.reset({
        title: campaign.title,
        description: campaign.description,
        contestant_id: campaign.contestant_id,
        goal_amount: campaign.goal_amount,
        start_date: new Date(campaign.start_date).toISOString().split('T')[0],
        end_date: new Date(campaign.end_date).toISOString().split('T')[0],
        status: campaign.status
      });
    }
  }, [campaign, form]);
  
  // Gérer la soumission du formulaire
  const onSubmit = async (data: CampaignFormValues) => {
    setIsSubmitting(true);
    
    try {
      const tenantId = import.meta.env.VITE_TENANT_ID || '2';
      const campaignData = {
        ...data,
        tenant_id: tenantId
      };
      
      if (slug) {
        // Mode édition
        await campaignService.updateCampaign(campaign?.id, {
          ...campaignData,
          id: campaign?.id,
          collected_amount: campaign?.collected_amount || 0
        });
        
        toast({
          title: "Campagne mise à jour",
          description: "La campagne a été mise à jour avec succès.",
          variant: "default"
        });
      } else {
        // Mode création
        await campaignService.createCampaign(campaignData);
        
        toast({
          title: "Campagne créée",
          description: "La campagne a été créée avec succès.",
          variant: "default"
        });
      }
      
      // Actualiser les données et rediriger
      queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] });
      navigate('/admin/crowdfunding');
      
    } catch (error) {
      console.error('Erreur lors de la soumission du formulaire:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'enregistrement de la campagne.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoadingCampaign && slug) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">
              {slug ? 'Modifier la campagne' : 'Nouvelle campagne'}
            </h1>
            <p className="text-gray-400">
              {slug ? 'Mettre à jour les informations de la campagne' : 'Créer une nouvelle campagne de financement participatif'}
            </p>
          </div>
          
          <Button variant="outline" onClick={() => navigate('/admin/crowdfunding')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour à la liste
          </Button>
        </div>
        
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-300">Titre de la campagne</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Entrez le titre de la campagne"
                        className="bg-black/20 border-white/20 text-white"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-300">Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Décrivez la campagne en détail..."
                        className="bg-black/20 border-white/20 text-white min-h-[150px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="contestant_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-300">Candidat</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={isLoadingContestants}
                      >
                        <FormControl>
                          <SelectTrigger className="bg-black/20 border-white/20 text-white">
                            {field.value
                              ? (
                                <>
                                  {contestants.find(c => c.id === field.value)?.name}
                                  {contestants.find(c => c.id === field.value)?.contest?.name
                                    ? ` (${contestants.find(c => c.id === field.value)?.contest?.name})`
                                    : ''}
                                </>
                              )
                              : <span className="text-gray-400">Sélectionnez un candidat</span>
                            }
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-black/90 border-white/10 text-white">
                          {contestants.map((contestant) => (
                            <SelectItem key={contestant.id} value={contestant.id}>
                              {contestant.name} ({contestant.contest?.name})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="goal_amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-300">Objectif (FCFA)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Montant à atteindre"
                          className="bg-black/20 border-white/20 text-white"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <FormField
                  control={form.control}
                  name="start_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-300">Date de début</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          className="bg-black/20 border-white/20 text-white [color-scheme:dark]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="end_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-300">Date de fin</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          className="bg-black/20 border-white/20 text-white [color-scheme:dark]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-300">Statut</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="bg-black/20 border-white/20 text-white">
                            <SelectValue placeholder="Sélectionnez un statut" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-black/90 border-white/10 text-white">
                          <SelectItem value="draft">Brouillon</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="completed">Terminée</SelectItem>
                          <SelectItem value="cancelled">Annulée</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <div className="flex justify-end">
                <Button 
                  type="submit" 
                  className="bg-yellow-500 hover:bg-yellow-600 text-black"
                  disabled={isSubmitting}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSubmitting ? 'Enregistrement...' : slug ? 'Mettre à jour' : 'Créer la campagne'}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </AdminLayout>
  );
};
